import React, { useState, useEffect } from 'react';
import { FileText, Clock, User, Calendar, Search, Filter, Eye, CheckCircle, AlertCircle } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandling';

const AssignedQuestions = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    loadAssignedQuestions();
  }, []);

  const loadAssignedQuestions = async () => {
    try {
      setLoading(true);
      // For now, we'll show placeholder data since the API might not be implemented yet
      setAssignments([
        {
          id: '1',
          title: 'Mathematics Foundation Review',
          description: 'Review calculus and algebra questions for the Mathematics Foundation KIT',
          assignedBy: 'Content Supervisor',
          kitName: 'Mathematics Foundation',
          questionCount: 25,
          completedCount: 15,
          status: 'In Progress',
          priority: 'High',
          dueDate: '2024-01-20T10:00:00Z',
          assignedAt: '2024-01-15T09:00:00Z',
          estimatedHours: 8
        },
        {
          id: '2',
          title: 'Physics Mechanics Review',
          description: 'Review mechanics problems and verify answer explanations',
          assignedBy: 'Content Supervisor',
          kitName: 'Physics Fundamentals',
          questionCount: 18,
          completedCount: 0,
          status: 'Pending',
          priority: 'Medium',
          dueDate: '2024-01-25T15:00:00Z',
          assignedAt: '2024-01-16T11:30:00Z',
          estimatedHours: 6
        },
        {
          id: '3',
          title: 'Chemistry Basics Review',
          description: 'Complete review of basic chemistry concepts and equations',
          assignedBy: 'Content Supervisor',
          kitName: 'Chemistry Essentials',
          questionCount: 12,
          completedCount: 12,
          status: 'Completed',
          priority: 'Low',
          dueDate: '2024-01-18T12:00:00Z',
          assignedAt: '2024-01-10T14:20:00Z',
          estimatedHours: 4
        }
      ]);
    } catch (error) {
      console.error('Error loading assigned questions:', error);
      showErrorToast(error, 'Failed to load assigned questions');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssignment = (assignment) => {
    showSuccessToast(`Starting work on: ${assignment.title}`);
    // Navigate to review interface
  };

  const handleViewDetails = (assignment) => {
    showSuccessToast(`Viewing details for: ${assignment.title}`);
    // Open assignment details modal
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    };
    return statusConfig[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return priorityConfig[priority] || 'bg-gray-100 text-gray-800';
  };

  const getProgressPercentage = (completed, total) => {
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getDaysUntilDue = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.kitName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !selectedStatus || assignment.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const statuses = [...new Set(assignments.map(a => a.status))];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Assignments</h1>
          <p className="text-gray-600 mt-1">Review assignments assigned to you by content supervisors</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            {assignments.filter(a => a.status === 'In Progress').length} in progress
          </div>
          <div className="text-sm text-gray-600">
            {assignments.filter(a => a.status === 'Pending').length} pending
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search assignments by title, description, or KIT name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Assignments Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex items-center justify-center h-64">
            <CardLoadingSpinner />
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No assignments found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedStatus
                ? 'Try adjusting your search criteria or filters.'
                : 'You have no assignments at the moment.'}
            </p>
          </div>
        ) : (
          filteredAssignments.map((assignment) => {
            const progressPercentage = getProgressPercentage(assignment.completedCount, assignment.questionCount);
            const daysUntilDue = getDaysUntilDue(assignment.dueDate);
            const isOverdue = daysUntilDue < 0;
            const isDueSoon = daysUntilDue <= 2 && daysUntilDue >= 0;

            return (
              <div key={assignment.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">{assignment.title}</h3>
                    <p className="text-sm text-gray-600">{assignment.description}</p>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(assignment.status)}`}>
                      {assignment.status}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(assignment.priority)}`}>
                      {assignment.priority}
                    </span>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>{assignment.completedCount}/{assignment.questionCount} questions</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{progressPercentage}% complete</div>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <FileText className="h-4 w-4 mr-2" />
                    <span>{assignment.kitName}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="h-4 w-4 mr-2" />
                    <span>Assigned by {assignment.assignedBy}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>{assignment.estimatedHours}h estimated</span>
                  </div>
                  <div className={`flex items-center text-sm ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-yellow-600' : 'text-gray-600'}`}>
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>
                      Due {new Date(assignment.dueDate).toLocaleDateString()}
                      {isOverdue && ' (Overdue)'}
                      {isDueSoon && !isOverdue && ` (${daysUntilDue} days left)`}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewDetails(assignment)}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center text-sm"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </button>
                  {assignment.status !== 'Completed' && (
                    <button
                      onClick={() => handleStartAssignment(assignment)}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm"
                    >
                      {assignment.status === 'Pending' ? 'Start Review' : 'Continue Review'}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AssignedQuestions;
